import { lakeIdAt } from "./data.js";
import { LAKE_IDS, distanceKm } from "./geo.js";

export function lakeLoadOrder(terrain, field, location) {
  const viewed = lakeIdAt(field, ...location);
  const distance = (lake) => {
    const [[south, west], [north, east]] = lake.bounds;
    const nearest = [Math.max(south, Math.min(north, location[0])), Math.max(west, Math.min(east, location[1]))];
    return distanceKm(location, nearest);
  };
  return [...terrain].sort((a, b) => {
    if (LAKE_IDS[a.slug] === viewed) return -1;
    if (LAKE_IDS[b.slug] === viewed) return 1;
    return distance(a) - distance(b);
  }).map((lake) => lake.slug);
}

export function temperatureRange(volumes) {
  const ranges = [...volumes].map((volume) => volume.temperature.range);
  return ranges.length ? [Math.min(...ranges.map(([low]) => low)), Math.max(...ranges.map(([, high]) => high))] : [0, 30];
}

export function temperatureBounds(volumes) {
  const bounds = [...volumes].map((volume) => volume.temperature.bounds).filter((range) => (
    Array.isArray(range) && range.length === 2 && range.every(Number.isFinite)
  ));
  return bounds.length ? [Math.min(...bounds.map(([low]) => low)), Math.max(...bounds.map(([, high]) => high))] : null;
}

// Only one download at a time; reconsider the camera before starting each
// pending lake. A new selection/hour invalidates even a late, uncancellable response.
export class VolumeLoader {
  constructor({ load, order, onLoad, onChange }) {
    Object.assign(this, { load, order, onLoad, onChange });
    this.cache = new Map();
    this.sequence = 0;
    this.controller = null;
    this.loading = null;
    this.pending = new Set();
    this.failed = new Map();
  }

  cancel() {
    this.sequence += 1;
    this.controller?.abort();
    this.loading = null;
    this.pending.clear();
  }

  async start(lakes, forecast, hour = new Date().toISOString().slice(0, 13)) {
    this.cancel();
    const sequence = this.sequence;
    this.pending = new Set(lakes);
    this.failed.clear();
    while (this.pending.size) {
      const lake = this.order().find((slug) => this.pending.has(slug)) || this.pending.values().next().value;
      this.loading = lake;
      this.controller = new AbortController();
      this.onChange();
      const key = `${lake}:${forecast}:${hour}`;
      let volume = this.cache.get(key);
      if (!volume) {
        try {
          volume = await this.load(lake, forecast, hour, this.controller.signal);
        } catch (error) {
          if (sequence !== this.sequence) return;
          this.failed.set(lake, error);
          this.pending.delete(lake);
          this.loading = null;
          this.onChange();
          continue;
        }
      }
      if (sequence !== this.sequence) return;
      // Partial volumes must be retried rather than freezing missing currents in the cache.
      if (volume.currents) {
        this.cache.delete(key);
        this.cache.set(key, volume);
        while (this.cache.size > 5) this.cache.delete(this.cache.keys().next().value);
      }
      this.pending.delete(lake);
      this.loading = null;
      this.onLoad(lake, volume);
      this.onChange();
    }
  }
}
